import { css } from '@emotion/react';
import React, { useEffect, useState } from 'react';
import t from 'react-translate';
import NvDropdown, { NvDropdownButtonStyle } from 'shared/components/inputs/nv-dropdown';
import ValidationErrorMessage from 'shared/components/inputs/validation-error-message';
import NvIcon from 'shared/components/nv-icon';
import NvPopover from 'shared/components/nv-popover';
import { danger, gray3 } from 'styles/global_defaults/colors';
import { halfSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';

const MetadataNvDropdown = ({
  field,
  courseMetadataTags,
  setMetadataTags,
  setRequiredField = null,
  removeRequiredField = null,
  showError,
  showTooltipError = false,
  nvDropDownName,
}) => {
  const { required } = field;
  const courseMetadataTag = courseMetadataTags.find((courseField) => courseField.metadataFieldId === field.id);

  useEffect(() => {
    if (required && setRequiredField) {
      setRequiredField(nvDropDownName, courseMetadataTag?.metadataValueId);
    }
    if (!required && removeRequiredField) {
      removeRequiredField(nvDropDownName);
    }
  }, [required, courseMetadataTag?.metadataValueId]);

  const defaultSelected = field.metadataValues.findIndex(value => (value.id === courseMetadataTag?.metadataValueId));
  const [selectedValueIndex, setSelectedValueIndex] = useState(defaultSelected);
  const [show, setShow] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const style = css`
    &.nv-custom-dropdown {
      display: inline-block;
      width: 100%;
      .metadata-title {
        width: 100%;
        padding: ${halfSpacing}px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .bs4-dropdown {
        border: 1px solid ${showError ? danger : gray3};
        border-radius: ${quarterSpacing}px;
      }
    }
  `;

  // Showing tooltip error only the first time after the form is submitted, and hide it when the dropdown is opened
  useEffect(() => {
    if (showTooltipError) {
      setShowTooltip(true);
    }
  }, [showTooltipError]);

  useEffect(() => {
    if (show) {
      setShowTooltip(false);
    }
  }, [show]);

  const getTitle = () => (
    <div className='metadata-title'>
      <div className={`ellipsis${selectedValueIndex < 0 ? ' text-gray-2' : ''}`}>
        {availableTags[selectedValueIndex + 1].text}
      </div>
      {showError && (
        <NvIcon
          icon='warning'
          size='smallest'
          className='text-danger mr-1 ml-auto'
        />
      )}
      <div className='icon text-xs icon-dropdown-arrow text-gray-3' />
    </div>
  );

  /**
   * Update the current list of selected metadata tags, and fire the metadata tag selected callback
   * @param {Object} item
   */
  const onTagClicked = (item = null, index = -1) => {
    let itemId;
    setSelectedValueIndex(index);
    if (item) {
      const existentTag = getExistingTag(courseMetadataTag, field.id);
      if (existentTag) {
        existentTag.metadataValueId = item.id;
      } else {
        courseMetadataTags.push({
          metadataValueId: item.id,
          metadataFieldId: field.id,
        });
      }
      itemId = item.id;
    } else {
      const fieldIndex = courseMetadataTags.findIndex(tag => (field.id === tag.metadataFieldId));
      if (fieldIndex !== -1) {
        courseMetadataTags.splice(fieldIndex, 1);
      }
    }
    if (required && setRequiredField) {
      setRequiredField(nvDropDownName, itemId);
    }
    setMetadataTags(courseMetadataTags);
  };

  const availableTags = field.metadataValues?.map((value, index) => ({
    type: 'text',
    text: value.value,
    callback: () => onTagClicked(value, index),
    textClassName: 'ellipsis',
  }));
  availableTags.unshift({
    type: 'text',
    text: t.COURSES.FORM.ADDITIONAL_METADATA.PLEASE_SELECT_METADATA_TAG(),
    resetItem: true,
    callback: () => onTagClicked(),
    textClassName: 'text-gray-2',
  });
  /**
   * Returns a metadata object that matches with a given fieldId or with a fieldId from another
   * metadata object.
   * @param {Object} metadataTag
   * @param {number} fieldId
   * @returns {Object}
   */
  const getExistingTag = (metadataTag = null, fieldId) => {
    let existentTag;
    if (metadataTag) {
      existentTag = courseMetadataTags.find(courseMetadata => courseMetadata.metadataFieldId === metadataTag.metadataFieldId);
    } else {
      existentTag = courseMetadataTags.find(tag => tag.metadataFieldId === fieldId);
    }
    return existentTag;
  };


  return (
    <div css={style} className='nv-custom-dropdown'>
      <NvPopover
        show={(showTooltip || show) && showError}
        content={(
          <ValidationErrorMessage
            text={t.VALIDATION.REQUIRED()}
          />
        )}
        placement='top'
        offset={30}
      >
        <NvDropdown
          items={availableTags}
          buttonStyle={NvDropdownButtonStyle.CUSTOM}
          showSelectedIndicator
          initialIndex={defaultSelected + 1}
          customTarget={() => getTitle()}
          onToggle={setShow}
          show={show}
        />
      </NvPopover>
    </div>
  );
};

export default MetadataNvDropdown;
